import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { googleAnalyticsService, GA4Account, GA4Property } from '../../services/googleAnalytics';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import { 
  BarChart3, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Globe,
  Building,
  Clock,
  ExternalLink
} from 'lucide-react';

interface GA4PropertySelectionStepProps {
  data?: {
    selectedAccount?: GA4Account;
    selectedProperty?: GA4Property;
    isConnected?: boolean;
    completed?: boolean;
    projectId?: string;
  };
  onNext: (data: any) => void;
  onBack: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function GA4PropertySelectionStep({ data, onNext, onBack, onSkip }: GA4PropertySelectionStepProps) {
  const [accounts, setAccounts] = useState<GA4Account[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<string>(data?.selectedAccount?.accountId || '');
  const [selectedProperty, setSelectedProperty] = useState<string>(data?.selectedProperty?.propertyId || '');
  const [isConnected, setIsConnected] = useState(false);
  const { updateProfile } = useAuth();
  
  console.log('GA4PropertySelectionStep mounted', { 
    data, 
    isConnected, 
    projectId: data?.projectId,
    hasProjectId: !!data?.projectId 
  });

  // Check if OAuth was completed and load accounts for manual selection
  useEffect(() => {
    const checkOAuthAndLoadAccounts = async () => {
      try {
        // Check if OAuth was completed
        const oauthCompleted = sessionStorage.getItem('oauth_completed');
        console.log('Checking OAuth completion:', oauthCompleted);
        
        if (oauthCompleted === 'true') {
          console.log('OAuth completed, loading accounts for manual selection');
          sessionStorage.removeItem('oauth_completed');
          setIsConnected(true);
          await loadAccounts();
        } else {
          // Assume OAuth is connected and try to load accounts
          console.log('No OAuth flag found, attempting to load accounts anyway');
          setIsConnected(true);
          await loadAccounts();
        }
      } catch (error) {
        console.error('Error in OAuth check:', error);
        setIsConnected(false);
        setIsLoadingAccounts(false);
        // Don't redirect immediately, let user see the error state
      }
    };

    checkOAuthAndLoadAccounts();
  }, []);

  const loadAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      console.log('Loading GA4 accounts...');
      const accountsData = await googleAnalyticsService.getAccounts();
      console.log('Loaded accounts:', accountsData);
      setAccounts(accountsData);
      
      // Auto-select first account/property if only one exists
      if (accountsData.length === 1) {
        setSelectedAccount(accountsData[0].accountId);
        if (accountsData[0].properties.length === 1) {
          setSelectedProperty(accountsData[0].properties[0].propertyId);
        }
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Failed to load Google Analytics accounts');
      throw error; // Re-throw so calling code can handle it
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const handleCompleteConnection = async () => {
    if (!selectedAccount || !selectedProperty) {
      toast.error('Please select both an account and property');
      return;
    }

    const selectedAccountData = accounts.find(acc => acc.accountId === selectedAccount);
    const selectedPropertyData = selectedAccountData?.properties.find(prop => prop.propertyId === selectedProperty);

    if (!selectedAccountData || !selectedPropertyData) {
      toast.error('Selected account or property not found');
      return;
    }

    // Get project ID - priority: data.projectId, then try to get it from session storage or current context
    let projectId = data?.projectId;
    
    console.log('Completing GA4 connection with:', {
      selectedAccount: selectedAccountData,
      selectedProperty: selectedPropertyData,
      projectId: projectId,
      hasProjectId: !!projectId
    });

    // Always try to connect the property during onboarding
    try {
      toast.loading('Connecting your GA4 property...');
      
      const connectResult = await googleAnalyticsService.connectProperty(
        selectedAccountData.accountId,
        selectedPropertyData.propertyId,
        selectedPropertyData.propertyName,
        projectId // Pass projectId even if undefined - backend should handle this
      );
      
      console.log('Connect result:', connectResult);
      console.log('Project ID available for onboarding completion:', connectResult.data?.projectId);
      
      if (connectResult.success) {
        toast.dismiss();
        toast.success(`🎉 Successfully connected ${selectedPropertyData.propertyName}!`);
        
        // Mark onboarding as completed using the dedicated backend endpoint
        if (connectResult.data?.projectId) {
          try {
            console.log('Completing onboarding via backend...');
            toast.loading('Finalizing your setup...');
            
            const onboardingResult = await googleAnalyticsService.completeOnboarding(connectResult.data.projectId);
            
            toast.dismiss();
            
            if (onboardingResult.success) {
              console.log('Onboarding completed successfully via backend');
              toast.success('🎉 Onboarding completed successfully!');
              
              // Force a profile refresh by triggering a re-fetch
              // The profile will be automatically refreshed when the user navigates
              console.log('Onboarding completion successful, profile will refresh on navigation');
            } else {
              console.error('Failed to complete onboarding via backend:', onboardingResult.error);
              // Fallback to manual profile update
              const updateResult = await updateProfile({ onboarding_completed: true });
              if (updateResult.error) {
                console.error('Failed to mark onboarding as complete:', updateResult.error);
                toast.warning('GA4 connected successfully, but failed to update onboarding status');
              } else {
                console.log('Onboarding marked as completed via manual profile update');
                toast.success('🎉 Setup completed!');
              }
            }
          } catch (error) {
            console.error('Error during onboarding completion:', error);
            // Fallback to manual profile update
            try {
              const updateResult = await updateProfile({ onboarding_completed: true });
              if (updateResult.error) {
                console.error('Failed to mark onboarding as complete:', updateResult.error);
                toast.warning('GA4 connected successfully, but failed to update onboarding status');
              } else {
                console.log('Onboarding marked as completed via fallback');
                toast.success('🎉 Setup completed!');
              }
            } catch (fallbackError) {
              console.error('Fallback onboarding completion failed:', fallbackError);
              toast.warning('GA4 connected successfully, but failed to update onboarding status');
            }
          }
        } else {
          // No project ID available, use manual profile update
          console.log('No project ID available, using manual onboarding completion...');
          try {
            const updateResult = await updateProfile({ onboarding_completed: true });
            if (updateResult.error) {
              console.error('Failed to mark onboarding as complete:', updateResult.error);
              toast.warning('GA4 connected successfully, but failed to update onboarding status');
            } else {
              console.log('Onboarding marked as completed manually');
              toast.success('🎉 Setup completed!');
            }
          } catch (error) {
            console.error('Failed to mark onboarding as complete:', error);
            toast.warning('GA4 connected successfully, but failed to update onboarding status');
          }
        }
        
        // Pass the connection result to the parent
        onNext({
          completed: true,
          selectedAccount: selectedAccountData,
          selectedProperty: selectedPropertyData,
          isConnected: true,
          connectionMethod: 'manual',
          connectionDetails: connectResult.data,
          onboardingCompleted: true
        });
        
        // Show background processing message
        setTimeout(() => {
          toast.info('🔧 Setting up your GA4 metadata in the background - this may take a few moments');
        }, 1000);
        
        return;
      } else {
        throw new Error(connectResult.error || 'Failed to connect GA4 property');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Failed to connect GA4 property:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to connect GA4 property: ${errorMessage}. You can try again or connect later from project settings.`);
      return;
    }
  };

  const handleSkipConnection = () => {
    onNext({ skipped: true });
  };

  const getSelectedPropertyData = () => {
    const account = accounts.find(acc => acc.accountId === selectedAccount);
    return account?.properties.find(prop => prop.propertyId === selectedProperty);
  };

  // Early return for debugging
  if (!isConnected && !isLoadingAccounts) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            OAuth Connection Required
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Please complete the Google Analytics connection first.
          </p>
          <Button onClick={onBack} variant="outline">
            Go Back to Connect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <BarChart3 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Select Your GA4 Property
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Choose which Google Analytics property you want to connect to your project
        </p>
      </div>

      {!isConnected && (
        <Card className="p-6 text-center border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">OAuth Connection Required</h3>
          <p className="text-yellow-600 dark:text-yellow-300 mb-4">
            Please complete the Google Analytics connection first.
          </p>
          <Button onClick={onBack} variant="outline">
            Go Back to Connect
          </Button>
        </Card>
      )}

      {isConnected && (
        <div className="space-y-6">
          <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
              <span className="text-green-800 dark:text-green-200 font-medium">Successfully connected to Google Analytics!</span>
            </div>
          </Card>

          {isLoadingAccounts ? (
            <Card className="p-6 text-center">
              <RefreshCw className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Loading Your Analytics Accounts</h3>
              <p className="text-gray-600 dark:text-gray-300">Please wait while we fetch your GA4 properties...</p>
            </Card>
          ) : accounts.length > 0 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Google Analytics Account
                </label>
                <Select
                  value={selectedAccount}
                  onValueChange={setSelectedAccount}
                  placeholder="Choose an account"
                  options={accounts.map((account) => ({
                    value: account.accountId,
                    label: account.accountName
                  }))}
                />
              </div>

              {selectedAccount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select GA4 Property
                  </label>
                  <Select
                    value={selectedProperty}
                    onValueChange={setSelectedProperty}
                    placeholder="Choose a property"
                    options={accounts
                      .find(acc => acc.accountId === selectedAccount)
                      ?.properties.map((property) => ({
                        value: property.propertyId,
                        label: property.propertyName
                      })) || []}
                  />
                </div>
              )}

              {/* Property Details */}
              {selectedProperty && (
                <Card className="p-4 bg-gray-50 dark:bg-gray-800">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Property Details</h4>
                  {(() => {
                    const property = getSelectedPropertyData();
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <Building className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">Name:</span>
                          <span className="ml-2 text-gray-900 dark:text-white font-medium">{property?.propertyName}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">Property ID:</span>
                          <span className="ml-2 text-gray-900 dark:text-white font-mono">{property?.propertyId}</span>
                        </div>
                        {property?.websiteUrl && (
                          <div className="flex items-center text-sm">
                            <ExternalLink className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                            <span className="text-gray-600 dark:text-gray-400">Website:</span>
                            <span className="ml-2 text-blue-600 dark:text-blue-400 hover:underline">{property.websiteUrl}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm">
                          <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">Timezone:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">{property?.timeZone || 'Not specified'}</span>
                        </div>
                      </div>
                    );
                  })()}
                </Card>
              )}
            </div>
          ) : (
            <Card className="p-6 text-center border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
              <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">No GA4 Properties Found</h3>
              <p className="text-yellow-600 dark:text-yellow-300 mb-4">
                We couldn't find any GA4 properties in your Google Analytics account. Please make sure you have GA4 set up.
              </p>
              <Button onClick={loadAccounts} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </Card>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-center pt-6 border-t border-gray-200 dark:border-gray-700">
        {isConnected && selectedAccount && selectedProperty ? (
          <Button onClick={handleCompleteConnection} size="lg">
            Connect Selected Property
          </Button>
        ) : (
          <Button disabled size="lg">
            {isConnected ? 'Select Property to Continue' : 'Connect to Google Analytics First'}
          </Button>
        )}
      </div>
    </div>
  );
} 